<script setup lang="ts">
import { computed, ref, useTemplateRef } from 'vue';
import OverlayPanel from 'primevue/overlaypanel';

const props = defineProps<{
  field: any;
  rowData: any;
  settings: any;
}>();

const listPanel = useTemplateRef<InstanceType<typeof OverlayPanel> | null>('listPanel');

const isHoveringList = ref(false);

// NOTE: We remove the link from AF Staff values as they are internal users and should not be linked to a profile.
const isAfStaff = computed(() => {
  const userStaffName = props.field;
  return userStaffName === 'AF Staff';
});

const alerts = computed(() => props.rowData?.alert);
const bites = computed(() => props.rowData?.bites);
const facebookLink = computed(() => props.settings?.isFacebookLink && props.rowData.facebook_url);
const fallback = computed(() => props.settings?.render?.fallback);
const rfc = computed(() => props.rowData?.rfc);
const showStatusIconsBelowAnimalName = computed(() => Boolean(props.settings?.showStatusIconsBelowAnimalName));

// NOTE: When the primary field resolves to nothing, a column may name a second row key to show
// instead — used by Owner / Guardian, where a partner-owned animal has no person to look up.
const fallbackValue = computed(() => {
  if (props.field || !fallback.value?.sourceKey) return null;

  return props.rowData?.[fallback.value.sourceKey] ?? null;
});

const fallbackTargetRef = computed(() => {
  if (!fallbackValue.value) return null;

  const { href, targetKey } = fallback.value;
  const primary = props.rowData?.[targetKey?.[0]];

  if (primary && typeof primary === 'object')
    return primary[targetKey[1]] != null ? `${href}${primary[targetKey[1]]}` : null;

  return primary != null ? `${href}${primary}` : null;
});

// File paths go to the api's get-file endpoint and can hold `&`, so the path is encoded.
const fileRef = computed(() => {
  if (props.settings?.render?.type !== 'file') return null;

  const { href, targetKey } = props.settings.render;
  const path = props.rowData?.[targetKey?.[0]];

  return path ? `${href}${encodeURIComponent(path)}` : null;
});

const hasTargetKey = computed(() => {
  if (!props.settings?.render?.targetKey) return false;

  const { targetKey } = props.settings.render;

  if (targetKey.length > 1) {
    const primary = props.rowData[targetKey[0]];
    return (
      !!(primary && typeof primary === 'object' && primary[targetKey[1]]) ||
      !!(primary != null && (typeof primary === 'string' || typeof primary === 'number'))
    );
  }

  return !!props.rowData[targetKey[0]];
});

/** `render.listKey`: one link per item of that row array (`idKey` / `labelKey`, default `_id` / `full_name`). */
const linkList = computed(() => {
  const { href, idKey = '_id', labelKey = 'full_name', listKey } = props.settings?.render ?? {};
  const items = listKey ? props.rowData?.[listKey] : null;

  if (!Array.isArray(items)) return [];

  return items.map((item) => ({ id: String(item[idKey]), label: item[labelKey], to: `${href}${item[idKey]}` }));
});

const targetRef = computed(() => {
  if (!props.settings?.render?.targetKey) return null;

  const { href, targetKey, secondaryKey } = props.settings.render;

  if (secondaryKey) {
    const primary = props.rowData[targetKey[0]];
    const secondary = props.rowData[secondaryKey[0]];

    if (primary != null && secondary != null) return `${href}${primary}/${secondary}`;
    if (primary != null) return `${href}${primary}`;

    return null;
  }

  if (targetKey.length > 1) {
    const primary = props.rowData[targetKey[0]];
    if (primary && typeof primary === 'object' && primary[targetKey[1]] != null) {
      const value = primary[targetKey[1]];
      if (targetKey[0] === 'by_id' && value === 'other') return null;

      return `${href}${value}`;
    }

    if (primary != null && (typeof primary === 'string' || typeof primary === 'number')) {
      if (targetKey[0] === 'by_id' && primary === 'other') return null;
      return `${href}${primary}`;
    }

    return null;
  }

  if (props.rowData[targetKey[0]] != null) {
    const value = props.rowData[targetKey[0]];
    if (targetKey[0] === 'by_id' && value === 'other') return null;

    return `${href}${value}`;
  }

  return null;
});

const firstLink = computed(() => linkList.value[0]);
const hiddenLinkCount = computed(() => linkList.value.length - 1);

const showAnimalNameStatusIconRow = computed(
  () =>
    showStatusIconsBelowAnimalName.value &&
    (Boolean(alerts.value) || (rfc.value?.length ?? 0) > 0 || (bites.value?.length ?? 0) > 0)
);

// EVENTS
function onListEnter(event: Event) {
  if (hiddenLinkCount.value < 1) return;

  isHoveringList.value = true;
  listPanel.value?.show(event);
}

function onListLeave() {
  isHoveringList.value = false;

  setTimeout(() => (!isHoveringList.value ? listPanel.value?.hide() : ''), 100);
}

function onListPanelEnter() {
  isHoveringList.value = true;
}
</script>

<template>
  <div
    v-if="firstLink"
    class="flex min-w-0 items-center gap-1"
    @mouseenter="onListEnter"
    @mouseleave="onListLeave"
  >
    <RouterLink
      class="af-link truncate hover:underline"
      :to="firstLink.to"
    >
      {{ firstLink.label }}
    </RouterLink>

    <Tag
      v-if="hiddenLinkCount > 0"
      class="shrink-0 cursor-default rounded-lg py-[3px] text-[10px] font-semibold"
      severity="secondary"
      :value="`+${hiddenLinkCount}`"
    />

    <OverlayPanel
      ref="listPanel"
      @mouseenter="onListPanelEnter"
      @mouseleave="onListLeave"
    >
      <ul class="max-h-[450px] max-w-[300px] overflow-auto text-sm">
        <li
          v-for="item in linkList"
          class="border-t py-1 first:border-t-0 first:pt-0 last:pb-0"
          :key="item.id"
        >
          <RouterLink
            class="af-link hover:underline"
            :to="item.to"
          >
            {{ item.label }}
          </RouterLink>
        </li>
      </ul>
    </OverlayPanel>
  </div>

  <RouterLink
    v-else-if="!field && settings?.canBeUnknown && targetRef"
    class="af-link overflow-hidden hover:underline"
    :to="targetRef"
  >
    {{ $t('common.unknown') }}
  </RouterLink>

  <RouterLink
    v-else-if="fallbackValue && fallbackTargetRef"
    class="af-link overflow-hidden text-ellipsis hover:underline"
    :to="fallbackTargetRef"
  >
    {{ fallbackValue }}
  </RouterLink>

  <p
    v-else-if="fallbackValue"
    class="overflow-hidden text-ellipsis"
  >
    {{ fallbackValue }}
  </p>

  <span
    v-else-if="!field"
    class="flex flex-col items-start gap-0.5"
  >
    {{ settings?.composedEmptyState ? $t(settings.composedEmptyState) : '-' }}
  </span>

  <a
    v-else-if="settings?.render?.type === 'mail'"
    class="af-link overflow-hidden hover:underline"
    target="_blank"
    :href="'mailto:' + field"
  >
    {{ field }}
  </a>

  <a
    v-else-if="fileRef"
    class="af-link overflow-hidden text-ellipsis hover:underline"
    rel="noopener"
    target="_blank"
    :href="fileRef"
  >
    {{ field }}
  </a>

  <div
    v-else-if="targetRef && !isAfStaff"
    :class="settings?.showStatusIconsBelowAnimalName ? 'flex min-w-0 flex-col items-start gap-1' : 'contents'"
  >
    <RouterLink
      class="af-link overflow-hidden text-ellipsis hover:underline"
      :to="targetRef"
    >
      {{ settings.hasName ? field.name : field }}
    </RouterLink>

    <div
      v-if="showAnimalNameStatusIconRow"
      class="flex flex-row flex-wrap items-center gap-1.5 leading-none"
    >
      <span
        v-if="alerts"
        v-tooltip.top="alerts"
        class="inline-flex h-3.5 shrink-0 items-center justify-center text-[14px] leading-none text-warning-400"
      >
        <FaIcon :icon="$icons.faTriangleExclamation" />
      </span>
      <span
        v-if="rfc?.length"
        v-tooltip.top="$t('animals.tooltips.requestForCare', { request: rfc.length })"
        class="inline-flex h-3.5 shrink-0 items-center justify-center text-[14px] leading-none text-red-500"
      >
        <FaIcon :icon="$icons.faHandHoldingMedical" />
      </span>
      <span
        v-if="bites?.length"
        v-tooltip.top="$t('animals.tooltips.biteReports', { count: bites.length })"
        class="inline-flex h-3.5 w-[22px] shrink-0 items-center justify-center text-warning-500"
      >
        <AIconBite />
      </span>
    </div>
  </div>

  <a
    v-else-if="facebookLink"
    class="af-link overflow-hidden text-ellipsis hover:underline"
    target="_blank"
    :href="facebookLink"
  >
    {{ $t('components.dataTable.facebookPage') }}
  </a>

  <p
    v-else-if="!hasTargetKey"
    class="overflow-hidden text-ellipsis"
  >
    {{ field }}
  </p>

  <p
    v-else
    class="overflow-hidden text-ellipsis"
  >
    {{ field || '-' }}
  </p>
</template>
